import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import {
  Activity,
  ArrowRightLeft,
  Bot,
  CircleAlert,
  MailCheck,
  MailWarning,
  MailPlus,
  Pencil,
  Plug,
  Scale,
  SquarePen,
  UserPlus,
  Sparkles,
} from 'lucide-react'
import { Badge, Button, cn } from '@ludus/ui'
import type { LeadId, PipelineId } from '@ludus/core'
import { fetchLeadActivity, type LeadActivityEvent } from '@/lib/api/lead-activity.ts'

/**
 * The lead's timeline, read from the event spine.
 *
 * Two rules shape this file, and both are about honesty rather than looks.
 *
 * **It never claims an order it does not have.** Several events from one save
 * share a timestamp to the millisecond, and the engine tie-breaks them on a
 * random id — so within one instant there is genuinely no "first". They are
 * therefore grouped *under* a single time marker as siblings, with no
 * connector, no numbering and no "then". The day a `seq` column exists this can
 * become a true sequence; until then, implying one would be a lie the UI tells
 * on the data's behalf.
 *
 * **It renders types it has never heard of.** The vocabulary is closed for
 * writes and open for reads: this bundle will meet rows written before it
 * shipped and rows written by a newer engine. Unknown types fall back to a
 * readable generic line rather than vanishing or throwing, which is what keeps
 * an engine deploy from requiring a console deploy.
 */

interface ActivityTabProps {
  pipelineId: PipelineId
  leadId: LeadId
}

type Presenter = {
  icon: typeof Activity
  /** i18n key under `pages.leads.detail.activity.event`. */
  key: string
  tone: 'neutral' | 'move' | 'people' | 'warn'
}

/**
 * Partial by design — `Partial<Record<…>>` in spirit, a plain lookup in fact.
 * A missing entry is a normal condition, not a bug.
 */
const PRESENTERS: Record<string, Presenter> = {
  'lead.received': { icon: Sparkles, key: 'received', tone: 'neutral' },
  'lead.status_changed': { icon: ArrowRightLeft, key: 'statusChanged', tone: 'move' },
  'lead.assigned': { icon: UserPlus, key: 'assigned', tone: 'people' },
  'lead.updated': { icon: Pencil, key: 'updated', tone: 'neutral' },
  'lead.note_added': { icon: SquarePen, key: 'noteAdded', tone: 'neutral' },
  // The three mail rows (#377). `scheduled` is deliberately `neutral` and not
  // `warn`: a mail waiting to go out is the normal middle of the sequence, and
  // an amber row for it would cry wolf on every send. Only a failure is amber.
  'lead.email_scheduled': { icon: MailPlus, key: 'emailScheduled', tone: 'neutral' },
  'lead.email_sent': { icon: MailCheck, key: 'emailSent', tone: 'people' },
  'lead.email_failed': { icon: MailWarning, key: 'emailFailed', tone: 'warn' },
  'distribution.round_completed': { icon: Scale, key: 'roundCompleted', tone: 'people' },
  'distribution.no_candidates': { icon: CircleAlert, key: 'noCandidates', tone: 'warn' },
  'distribution.unknown_status': { icon: CircleAlert, key: 'unknownStatus', tone: 'warn' },
  'distribution.assignment_unroutable': { icon: CircleAlert, key: 'unroutable', tone: 'warn' },
  'pipeline.capability_gap': { icon: Plug, key: 'capabilityGap', tone: 'warn' },
}

const TONE_CLASS: Record<Presenter['tone'], string> = {
  neutral: 'bg-primary/15 text-primary',
  move: 'bg-sky-500/15 text-sky-500',
  people: 'bg-emerald-500/15 text-emerald-500',
  warn: 'bg-amber-500/15 text-amber-500',
}

export function ActivityTab({ pipelineId, leadId }: ActivityTabProps) {
  const { t, i18n } = useTranslation()
  // Pages accumulate client-side: the cursor is opaque and forward-only, so
  // "load older" appends rather than replacing. SWR keys the first page only —
  // later pages are not cache entries, they are more of this one.
  const [older, setOlder] = useState<LeadActivityEvent[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)

  const { data, error, isLoading } = useSWR(
    ['lead-activity', pipelineId, leadId],
    () => fetchLeadActivity(pipelineId, leadId),
    { revalidateOnFocus: false },
  )

  const events = data ? [...data.items, ...older] : []
  const next = cursor ?? data?.nextCursor ?? null

  const loadMore = async () => {
    if (!next) return
    setLoadingMore(true)
    try {
      const page = await fetchLeadActivity(pipelineId, leadId, { cursor: next })
      setOlder((prev) => [...prev, ...page.items])
      setCursor(page.nextCursor)
    } finally {
      setLoadingMore(false)
    }
  }

  if (error) {
    return (
      <Shell tone="error">
        {t('pages.leads.detail.activity.error', {
          defaultValue: 'The activity for this lead could not be read.',
        })}
      </Shell>
    )
  }

  if (isLoading) {
    return (
      <Shell>
        {t('pages.leads.detail.activity.loading', { defaultValue: 'Reading the activity…' })}
      </Shell>
    )
  }

  if (events.length === 0) {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-border/40 bg-background-secondary/40 px-4 py-3">
        <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
          <Activity className="h-4 w-4" aria-hidden="true" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-sm font-semibold text-foreground">
            {t('pages.leads.detail.tabs.activityEmpty.title', { defaultValue: 'No activity yet' })}
          </span>
          <span className="text-xs leading-relaxed text-muted">
            {t('pages.leads.detail.activity.emptyBody', {
              defaultValue: 'Everything that happens to this lead will appear here.',
            })}
          </span>
        </div>
      </div>
    )
  }

  const days = groupByDay(events, i18n.language)

  return (
    <div className="flex flex-col gap-5">
      {days.map((day) => (
        <section key={day.label} className="flex flex-col gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">{day.label}</h3>
          <ol className="flex flex-col gap-1.5">
            {day.instants.map((instant) => (
              <li key={instant.at} className="flex gap-3">
                <time
                  className="w-12 shrink-0 pt-2 text-right text-xs tabular-nums text-muted"
                  dateTime={new Date(instant.at).toISOString()}
                >
                  {formatTime(instant.at, i18n.language)}
                </time>
                {/*
                  Siblings under one time, never a sequence: these share a
                  millisecond and the log cannot say which came first.
                */}
                <ul className="flex min-w-0 flex-1 flex-col gap-1.5">
                  {instant.events.map((event) => (
                    <ActivityRow key={event.id} event={event} />
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        </section>
      ))}

      {next !== null && (
        <div>
          <Button variant="secondary" size="sm" onClick={loadMore} disabled={loadingMore}>
            {loadingMore
              ? t('pages.leads.detail.activity.loadingMore', { defaultValue: 'Loading…' })
              : t('pages.leads.detail.activity.loadOlder', { defaultValue: 'Load older' })}
          </Button>
        </div>
      )}
    </div>
  )
}

function ActivityRow({ event }: { event: LeadActivityEvent }) {
  const { t } = useTranslation()
  const presenter = PRESENTERS[event.type]
  const Icon = presenter?.icon ?? Activity
  const tone = presenter?.tone ?? 'neutral'

  // An unknown type still reads as something: the raw type is a poor label but
  // an honest one, and it is strictly better than a blank row or a crash.
  const text = presenter
    ? t(`pages.leads.detail.activity.event.${presenter.key}`, {
        defaultValue: FALLBACK_TEXT[presenter.key] ?? event.type,
        ...describe(event),
      })
    : t('pages.leads.detail.activity.event.unknown', {
        defaultValue: 'Recorded: {{type}}',
        type: event.type,
      })

  return (
    <li className="flex items-start gap-2.5 rounded-md border border-border/40 bg-background-secondary/40 px-3 py-2">
      <span
        className={cn(
          'mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
          TONE_CLASS[tone],
        )}
      >
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1 text-sm leading-relaxed text-foreground">{text}</span>
      <ActorBadge actor={event.actor} />
    </li>
  )
}

/**
 * Who did it, as a phrase rather than a token — and an automation is visibly
 * not a person. That distinction is the whole point of the `actor` column: a
 * robot appears in the same feed as a human, so a reader must be able to tell
 * them apart at a glance without a second timeline.
 */
function ActorBadge({ actor }: { actor: string | null }) {
  const { t } = useTranslation()
  if (actor === null || actor === 'system') return null

  if (actor.startsWith('automation:')) {
    return (
      <Badge tone="info" size="sm" className="shrink-0">
        <Bot className="mr-1 h-3 w-3" aria-hidden="true" />
        {t('pages.leads.detail.activity.actor.automation', { defaultValue: 'automation' })}
      </Badge>
    )
  }
  if (actor.startsWith('adapter:')) {
    return (
      <Badge tone="muted" size="sm" className="shrink-0">
        {t('pages.leads.detail.activity.actor.source', { defaultValue: 'source' })}
      </Badge>
    )
  }
  return (
    <Badge tone="muted" size="sm" className="shrink-0">
      {t('pages.leads.detail.activity.actor.person', { defaultValue: 'a person' })}
    </Badge>
  )
}

const FALLBACK_TEXT: Record<string, string> = {
  received: 'Lead entered the pipeline',
  statusChanged: 'Moved to another stage',
  assigned: 'Assigned to a manager',
  updated: 'Edited {{fields}}',
  noteAdded: 'A note was added',
  roundCompleted: 'Distribution picked an owner',
  noCandidates: 'Distribution found no eligible manager',
  unknownStatus: 'Distribution met a stage this pipeline has never had',
  unroutable: 'The chosen manager could not be written back to the source',
  capabilityGap: 'The source does not support this operation',
  emailScheduled: 'Queued the {{template}} e-mail to {{to}}',
  emailSent: 'Sent the {{template}} e-mail to {{to}}',
  emailFailed: 'Could not send the {{template}} e-mail to {{to}} — {{error}}',
}

/** Interpolation values a presenter may use. Nothing here may throw. */
function describe(event: LeadActivityEvent): Record<string, string> {
  const payload = (event.payload ?? {}) as Record<string, unknown>
  const changed = Array.isArray(payload.changed) ? (payload.changed as string[]) : []
  // Read defensively, like `changed` above: this payload was written by an
  // engine that may be newer or older than this bundle, and a row must render
  // even when a field it expected is absent.
  const str = (value: unknown) => (typeof value === 'string' ? value : '')
  return {
    fields: changed.join(', '),
    // The sink's own template name, shown verbatim. The console has no
    // dictionary of mail names and must not grow one — that word belongs to
    // whatever sends the mail (#377).
    template: str(payload.template),
    to: str(payload.to),
    error: str(payload.error),
  }
}

interface Instant {
  at: number
  events: LeadActivityEvent[]
}

interface Day {
  label: string
  instants: Instant[]
}

/**
 * Day, then instant. The second level is what stops the UI implying an order
 * the data does not carry.
 */
function groupByDay(events: LeadActivityEvent[], locale: string): Day[] {
  const days: Day[] = []
  for (const event of events) {
    const label = formatDay(event.occurredAt, locale)
    let day = days.at(-1)
    if (!day || day.label !== label) {
      day = { label, instants: [] }
      days.push(day)
    }
    const instant = day.instants.at(-1)
    if (instant && instant.at === event.occurredAt) instant.events.push(event)
    else day.instants.push({ at: event.occurredAt, events: [event] })
  }
  return days
}

function formatDay(ms: number, locale: string): string {
  return new Date(ms).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function formatTime(ms: number, locale: string): string {
  return new Date(ms).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
}

function Shell({ children, tone }: { children: React.ReactNode; tone?: 'error' }) {
  return (
    <div
      className={cn(
        'rounded-lg border px-4 py-3 text-sm',
        tone === 'error'
          ? 'border-danger/40 bg-danger/5 text-danger'
          : 'border-border/40 bg-background-secondary/40 text-muted',
      )}
    >
      {children}
    </div>
  )
}
